import React, { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Info,
  CheckCircle2,
  ChevronRight,
  Lightbulb,
  ClipboardList,
} from "lucide-react";
import { Deck } from "@/types/linguadeck";
import { getLocalizedTestData } from "@/lib/testData";

const LABELS = ["A", "B", "C", "D"];

interface TestModeViewProps {
  deck: Deck;
  onNext?: () => void;
  onMark?: (correct: boolean) => void;
}

export default function TestModeView({
  deck,
  onNext,
  onMark,
}: TestModeViewProps) {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || "en").split("-")[0];

  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [hintExpanded, setHintExpanded] = useState(false);
  const [cardIndex, setCardIndex] = useState(0);

  const cardsWithTestData = useMemo(
    () => deck.cards.filter((c) => getLocalizedTestData(c, lang)),
    [deck.cards, lang]
  );

  const currentCard = cardsWithTestData[cardIndex];
  const total = cardsWithTestData.length;
  const localized = currentCard
    ? getLocalizedTestData(currentCard, lang)
    : null;

  const isLocked = selectedIndex !== null;
  const isCorrect =
    isLocked && localized && selectedIndex === localized.correctIndex;

  const handleSelect = (idx: number) => {
    if (isLocked) return;
    setSelectedIndex(idx);
    if (localized && onMark) {
      onMark(idx === localized.correctIndex);
    }
  };

  const handleNext = () => {
    setSelectedIndex(null);
    setHintExpanded(false);
    setCardIndex((i) => (i + 1) % Math.max(1, total));
    onNext?.();
  };

  if (total === 0) {
    return (
      <div className="flex items-center justify-center min-h-[60vh] px-4">
        <div className="w-[95%] max-w-[550px] mx-auto bg-white dark:bg-[hsl(var(--card-bg))] rounded-[32px] shadow-[0_20px_50px_rgba(0,0,0,0.1)] p-10 text-center">
          <ClipboardList className="w-14 h-14 mx-auto mb-4 text-slate-300" />
          <p className="text-lg font-bold text-slate-800 dark:text-[hsl(var(--foreground))] mb-1">
            {t("testMode.noTestCards")}
          </p>
          <p className="text-sm text-slate-400 dark:text-[hsl(var(--muted-foreground))]">
            {t("testMode.noTestCardsHint")}
          </p>
        </div>
      </div>
    );
  }

  if (!currentCard || !localized) return null;

  const filledCount = cardIndex + (isLocked ? 1 : 0);
  const progressPct = Math.min(100, (filledCount / total) * 100);

  return (
    <div className="flex flex-col items-center justify-start flex-1 min-h-0 pt-4 pb-8 px-2">
      <div className="w-[95%] max-w-[550px] mt-2 mb-auto mx-auto bg-white dark:bg-[hsl(var(--card-bg))] rounded-[32px] shadow-[0_20px_50px_rgba(0,0,0,0.1)] overflow-hidden flex flex-col">
        {/* ── Header: progress bar ── */}
        <div className="px-6 pt-4 pb-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-slate-400 dark:text-[hsl(var(--muted-foreground))] tracking-wide uppercase">
              {deck.title}
            </span>
            <span className="text-xs font-bold text-slate-500 dark:text-[hsl(var(--muted-foreground))] tabular-nums">
              {cardIndex + 1} / {total}
            </span>
          </div>
          <div className="h-3 w-full rounded-full overflow-hidden bg-slate-100 dark:bg-[hsl(var(--border))]">
            <div
              className="h-full rounded-full bg-gradient-to-r from-indigo-500 to-purple-500 transition-all duration-500 ease-out"
              style={{ width: `${progressPct}%` }}
            />
          </div>
        </div>

        {/* ── Body: image + word + hint link ── */}
        <div className="flex flex-col items-center px-6 pb-1">
          {currentCard.imageUrl && (
            <div className="w-full max-w-[200px] max-h-[140px] rounded-2xl bg-slate-50 dark:bg-[hsl(var(--secondary))] flex items-center justify-center overflow-hidden mb-2">
              <img
                src={currentCard.imageUrl}
                alt=""
                className="max-h-[140px] w-auto object-contain"
              />
            </div>
          )}
          <h2 className="text-3xl font-black text-slate-800 dark:text-[hsl(var(--foreground))] text-center mb-1 leading-tight">
            {localized.question}
          </h2>

          {localized.hint && (
            <button
              type="button"
              onClick={() => setHintExpanded((v) => !v)}
              className="inline-flex items-center gap-1 text-xs font-medium text-indigo-400 hover:text-indigo-600 dark:text-indigo-400 dark:hover:text-indigo-300 transition-colors"
            >
              <Lightbulb className="w-3.5 h-3.5" />
              {t("testMode.hint")}
            </button>
          )}

          {hintExpanded && localized.hint && (
            <div className="w-full mt-1 mb-1 px-4 py-2 rounded-xl bg-indigo-50/80 dark:bg-indigo-950/30 border border-indigo-100 dark:border-indigo-800/40">
              <p className="text-sm text-slate-600 dark:text-[hsl(var(--foreground))] leading-relaxed text-center">
                {localized.hint}
              </p>
            </div>
          )}

          {isLocked && (
            <div
              className={`w-full mt-1.5 flex items-center justify-center gap-2 rounded-xl px-4 py-2 ${
                isCorrect
                  ? "bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-700/50"
                  : "bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-700/50"
              }`}
            >
              {isCorrect ? (
                <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-400" />
              ) : (
                <Info className="h-4 w-4 shrink-0 text-red-500 dark:text-red-400" />
              )}
              <p
                className={`text-sm font-semibold ${
                  isCorrect
                    ? "text-emerald-700 dark:text-emerald-300"
                    : "text-red-600 dark:text-red-300"
                }`}
              >
                {isCorrect
                  ? t("testMode.correctFeedback")
                  : t("testMode.incorrectFeedback")}
              </p>
            </div>
          )}
        </div>

        {/* ── Answer tiles (A, B, C, D) ── */}
        <div className="flex flex-col gap-y-2 px-6 pt-3 pb-3">
          {LABELS.slice(0, Math.min(4, localized.options.length)).map(
            (label, idx) => {
              const text = localized.options[idx] ?? "";
              const chosen = selectedIndex === idx;
              const correct = localized.correctIndex === idx;
              const showCorrect = isLocked && correct;
              const showWrong = isLocked && chosen && !correct;

              let tileBorder =
                "border-slate-100 dark:border-[hsl(var(--border))]";
              let tileBg = "bg-white dark:bg-[hsl(var(--card-bg))]";
              let hoverClass =
                "hover:border-indigo-400 hover:bg-indigo-50 dark:hover:border-indigo-500 dark:hover:bg-indigo-950/20 cursor-pointer active:scale-[0.98]";
              let badgeBg =
                "bg-slate-100 dark:bg-[hsl(var(--border))] text-slate-500 dark:text-[hsl(var(--muted-foreground))]";

              if (showCorrect) {
                tileBorder = "border-emerald-400 dark:border-emerald-500";
                tileBg = "bg-emerald-50 dark:bg-emerald-950/40";
                hoverClass = "cursor-default";
                badgeBg = "bg-emerald-500 text-white";
              } else if (showWrong) {
                tileBorder = "border-red-400 dark:border-red-500";
                tileBg = "bg-red-50 dark:bg-red-950/40";
                hoverClass = "cursor-default";
                badgeBg = "bg-red-500 text-white";
              } else if (isLocked) {
                hoverClass = "cursor-default opacity-60";
              }

              return (
                <button
                  key={idx}
                  type="button"
                  disabled={isLocked}
                  onClick={() => handleSelect(idx)}
                  className={`border-2 ${tileBorder} ${tileBg} rounded-2xl py-3 px-4 flex items-center gap-4 transition-all duration-200 w-full text-left ${hoverClass}`}
                >
                  <span
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold shrink-0 ${badgeBg}`}
                  >
                    {label}
                  </span>
                  <span className="text-base font-medium text-slate-700 dark:text-[hsl(var(--foreground))]">
                    {text}
                  </span>
                </button>
              );
            }
          )}
        </div>

        {/* ── Footer: full-width Next button ── */}
        <div className="px-6 pb-5 pt-1">
          <button
            type="button"
            onClick={handleNext}
            disabled={!isLocked}
            className="w-full py-4 rounded-2xl bg-indigo-600 text-white font-bold text-lg shadow-lg hover:bg-indigo-700 transition-transform active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed disabled:active:scale-100 flex items-center justify-center gap-2"
          >
            {t("testMode.next")}
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
}
